import React, { createContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
    const [courses, setCourses] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch Initial Data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const { data: coursesData, error: coursesError } = await supabase
                .from('courses')
                .select('*')
                .order('created_at', { ascending: false });

            if (coursesError) throw coursesError;
            setCourses(coursesData || []);

            const { data: jobsData, error: jobsError } = await supabase
                .from('jobs')
                .select('*')
                .order('created_at', { ascending: false });

            if (jobsError) throw jobsError;
            setJobs(jobsData || []);

            const { data: teamData, error: teamError } = await supabase
                .from('team_members')
                .select('*')
                .order('display_order', { ascending: true });

            if (teamError) throw teamError;
            setTeamMembers(teamData || []);

            const { data: projectsData, error: projectsError } = await supabase
                .from('projects')
                .select('*')
                .order('created_at', { ascending: false });

            if (projectsError) throw projectsError;
            setProjects(projectsData || []);

        } catch (error) {
            console.error('Error fetching data:', error.message);
        } finally {
            setLoading(false);
        }
    };

    // Actions
    const addCourse = async (course) => {
        try {
            // Remove any id if present to let DB handle it, and ensure we only send schema fields
            const { id, ...courseData } = course;
            const { data, error } = await supabase
                .from('courses')
                .insert([courseData])
                .select();

            if (error) throw error;
            if (data) setCourses([data[0], ...courses]);
            return { success: true };
        } catch (error) {
            console.error('Error adding course:', error.message);
            return { success: false, message: error.message };
        }
    };

    // Helper to extract storage path from URL
    const getPathFromUrl = (url) => {
        try {
            if (!url) return null;
            const urlObj = new URL(url);
            const pathSegments = urlObj.pathname.split('/');
            
            // Look for the bucket name in the path to handle potential subfolders
            // Standard Supabase path: /storage/v1/object/public/bucket-name/folder/file.ext
            const bucketIndex = pathSegments.indexOf('course-assets');
            
            if (bucketIndex !== -1 && bucketIndex < pathSegments.length - 1) {
                // Return everything after the bucket name
                return decodeURIComponent(pathSegments.slice(bucketIndex + 1).join('/'));
            }
            
            // Fallback: simpler extraction if bucket naming convention differs
            return decodeURIComponent(pathSegments.pop());
        } catch (e) {
            console.error('Error parsing URL for path extraction:', url, e);
            return null;
        }
    };

    const deleteCourse = async (id) => {
        try {
            // Find course to get thumbnail url
            const courseToDelete = courses.find(c => c.id === id);
            
            if (courseToDelete?.thumbnail_url) {
                try {
                    const filePath = getPathFromUrl(courseToDelete.thumbnail_url);
                    if (filePath) {
                        const { error: removeError } = await supabase.storage
                            .from('course-assets')
                            .remove([filePath]);

                        if (removeError) console.error('Supabase storage remove error:', removeError);
                    }
                } catch (err) {
                    console.error('Error deleting course thumbnail:', err);
                }
            }

            // Delete associated enrollments to satisfy foreign key constraint
            const { error: enrollmentDeleteError } = await supabase
                .from('enrollments')
                .delete()
                .eq('course_id', id);

            if (enrollmentDeleteError) {
                console.error('Error deleting associated enrollments:', enrollmentDeleteError);
                throw enrollmentDeleteError;
            }

            const { error } = await supabase
                .from('courses')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setCourses(courses.filter(course => course.id !== id));
            return { success: true };
        } catch (error) {
            console.error('Error deleting course:', error.message);
            return { success: false, message: error.message };
        }
    };

    const updateCourse = async (updatedCourse) => {
        try {
            // Extract id and created_at to avoid sending them in the update body
            const { id, created_at, ...updates } = updatedCourse;

            if (!id) {
                throw new Error("Cannot update course: Missing ID");
            }

            // Check if thumbnail has changed
            const oldCourse = courses.find(c => c.id === id);
            if (oldCourse?.thumbnail_url && oldCourse.thumbnail_url !== updates.thumbnail_url) {
                try {
                    const filePath = getPathFromUrl(oldCourse.thumbnail_url);
                    if (filePath) {
                        const { error: removeError } = await supabase.storage
                            .from('course-assets')
                            .remove([filePath]);
                        
                        if (removeError) console.error('Supabase storage remove error:', removeError);
                    }
                } catch (err) {
                    console.error('Error deleting old course thumbnail:', err);
                }
            }

            const { error } = await supabase
                .from('courses')
                .update(updates)
                .eq('id', id);

            if (error) throw error;

            // In local state, we use the full object including ID
            setCourses(courses.map(course => (course.id === id ? { ...course, ...updates } : course)));
            return { success: true };
        } catch (error) {
            console.error('Error updating course:', error.message);
            return { success: false, message: error.message };
        }
    };

    const addJob = async (job) => {
        try {
            // Clean up job object. 'role' property was added in AdminDashboard but not in schema.
            // Also remove id if present.
            const { id, role, ...jobData } = job;
            const { data, error } = await supabase
                .from('jobs')
                .insert([jobData])
                .select();

            if (error) throw error;
            if (data) setJobs([data[0], ...jobs]);
            return { success: true };
        } catch (error) {
            console.error('Error adding job:', error.message);
            return { success: false, message: error.message };
        }
    };

    const deleteJob = async (id) => {
        try {
            const { error } = await supabase
                .from('jobs')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setJobs(jobs.filter(job => job.id !== id));
            return { success: true };
        } catch (error) {
            console.error('Error deleting job:', error.message);
            return { success: false, message: error.message };
        }
    };

    const updateJob = async (updatedJob) => {
        try {
            // Remove role if present to avoid schema error during update
            const { role, ...cleanJob } = updatedJob;
            const { error } = await supabase
                .from('jobs')
                .update(cleanJob)
                .eq('id', updatedJob.id);

            if (error) throw error;
            setJobs(jobs.map(job => (job.id === updatedJob.id ? updatedJob : job)));
            return { success: true };
        } catch (error) {
            console.error('Error updating job:', error.message);
            return { success: false, message: error.message };
        }
    };

    const addTeamMember = async (member) => {
        try {
            const { id, ...memberData } = member;
            const { data, error } = await supabase
                .from('team_members')
                .insert([memberData])
                .select();

            if (error) throw error;
            if (data) setTeamMembers([...teamMembers, data[0]]);
            return { success: true };
        } catch (error) {
            console.error('Error adding team member:', error.message);
            return { success: false, message: error.message };
        }
    };

    const deleteTeamMember = async (id) => {
        try {
            // Find member to get image url
            const memberToDelete = teamMembers.find(m => m.id === id);
            console.log('Attempting to delete member:', memberToDelete); 
            
            if (memberToDelete?.image_url) {
                try {
                    const filePath = getPathFromUrl(memberToDelete.image_url);
                    console.log('Extracted file path for deletion:', filePath); 
                    
                    if (filePath) {
                        const { data: removeData, error: removeError } = await supabase.storage
                            .from('course-assets')
                            .remove([filePath]);

                        if (removeError) {
                            console.error('Supabase storage remove error:', removeError);
                            // Try deleting assuming the file might be just the name if logic failed
                            // This is a last resort fallback safe to attempt
                        } else {
                            console.log('Supabase storage remove success:', removeData);
                        }
                    }
                } catch (err) {
                    console.error('Error executing image deletion:', err);
                }
            }

            const { error } = await supabase
                .from('team_members')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setTeamMembers(teamMembers.filter(member => member.id !== id));
            return { success: true };
        } catch (error) {
            console.error('Error deleting team member:', error.message);
            return { success: false, message: error.message };
        }
    };

    const updateTeamMember = async (updatedMember) => {
        try {
            const { id, created_at, ...updates } = updatedMember;

            // Check if image has changed
            const oldMember = teamMembers.find(m => m.id === id);
            
            console.log('Updating member. ID:', id);
            console.log('Old URL:', oldMember?.image_url);
            console.log('New URL:', updates.image_url);

            if (oldMember?.image_url && oldMember.image_url !== updates.image_url) {
                console.log('Image change detected. Initiating cleanup...');
                try {
                    const filePath = getPathFromUrl(oldMember.image_url);
                    console.log('Target file path for matching OLD image:', filePath);

                    if (filePath) {
                        const { data: removeData, error: removeError } = await supabase.storage
                            .from('course-assets')
                            .remove([filePath]);
                        
                        if (removeError) console.error('Supabase storage remove error:', removeError);
                        else console.log('Supabase storage remove success:', removeData);
                    } else {
                        console.warn('Could not extract file path from URL:', oldMember.image_url);
                    }

                } catch (err) {
                    console.error('Error during old image deletion:', err);
                }
            }

            const { error } = await supabase
                .from('team_members')
                .update(updates)
                .eq('id', id);

            if (error) throw error;
            setTeamMembers(teamMembers.map(member => (member.id === id ? { ...member, ...updates } : member)));
            return { success: true };
        } catch (error) {
            console.error('Error updating team member:', error.message);
            return { success: false, message: error.message };
        }
    };

    // Projects CRUD
    const addProject = async (project) => {
        try {
            const { id, ...projectData } = project;
            const { data, error } = await supabase
                .from('projects')
                .insert([projectData])
                .select();

            if (error) throw error;
            if (data) setProjects([data[0], ...projects]);
            return { success: true };
        } catch (error) {
            console.error('Error adding project:', error.message);
            return { success: false, message: error.message };
        }
    };

    const deleteProject = async (id) => {
        try {
            const projectToDelete = projects.find(p => p.id === id);
            
            if (projectToDelete?.image_url) {
                try {
                    const filePath = getPathFromUrl(projectToDelete.image_url);
                    if (filePath) {
                        const { error: removeError } = await supabase.storage
                            .from('course-assets')
                            .remove([filePath]);

                        if (removeError) console.error('Error removing project image:', removeError);
                    }
                } catch (err) {
                    console.error('Error deleting project image:', err);
                }
            }

            const { error } = await supabase
                .from('projects')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setProjects(projects.filter(p => p.id !== id));
            return { success: true };
        } catch (error) {
            console.error('Error deleting project:', error.message);
            return { success: false, message: error.message };
        }
    };

    const updateProject = async (updatedProject) => {
        try {
            const { id, created_at, ...updates } = updatedProject;

            const oldProject = projects.find(p => p.id === id);
            if (oldProject?.image_url && oldProject.image_url !== updates.image_url) {
                try {
                    const filePath = getPathFromUrl(oldProject.image_url);
                    if (filePath) {
                        const { error: removeError } = await supabase.storage
                            .from('course-assets')
                            .remove([filePath]);
                        if (removeError) console.error('Error removing old project image:', removeError);
                    }
                } catch (err) {
                    console.error('Error processing old project image deletion:', err);
                }
            }

            const { error } = await supabase
                .from('projects')
                .update(updates)
                .eq('id', id);

            if (error) throw error;
            setProjects(projects.map(p => (p.id === id ? { ...p, ...updates } : p)));
            return { success: true };
        } catch (error) {
            console.error('Error updating project:', error.message);
            return { success: false, message: error.message };
        }
    };

    return (
        <DataContext.Provider value={{
            courses, addCourse, deleteCourse, updateCourse,
            jobs, addJob, deleteJob, updateJob,
            teamMembers, addTeamMember, deleteTeamMember, updateTeamMember,
            projects, addProject, deleteProject, updateProject,
            loading
        }}>
            {children}
        </DataContext.Provider>
    );
};
